"use client";
import React, {
  useEffect,
  useState,
  useRef,
  SetStateAction,
  Dispatch,
  useCallback,
} from "react";
import { usePathname } from "next/navigation";
import styles from "@styles/nav.module.css";
import {
  ArrowDown,
  BlackArrowDown,
  BlackLogo,
  BlackLogoMobile,
  CloseIcon,
  HamburgerIcon,
  Logo,
  LogoMobile,
  MailIcon,
  SearchIcon,
  BlackSearchIcon,
} from "@icons/svgs";
import Link from "next/link";
import Modal from "../../modal/Modal";
import { GraduationCap } from "lucide-react";
import { Button } from "@ui/button";

type SearchProps = {
  lightBg: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  openModal: boolean;
};
type NavProps = {
  lightBg: boolean;
  bg?: boolean;
};
type MenuHarburgerProps = {
  navState: boolean;
  openNav: () => void;
  closeNav: () => void;
  lightBg: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  openModal: boolean;
  hamburgerRef: React.RefObject<HTMLButtonElement>;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Nav({ bg, lightBg }: NavProps) {
  const pathname = usePathname();
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const sidenavRef = useRef<HTMLDivElement>(null);
  const navTrayRef = useRef<HTMLDivElement>(null);
  const [scrolling, setScrolling] = useState<boolean>(false);
  const [navState, setNavState] = useState<boolean>(false);
  const [admissionDropdown, setAdmissionDropdown] = useState<boolean>(false);
  const [aboutDropdown, setAboutDropdown] = useState<boolean>(false);
  const [studentDropdown, setStudentDropdown] = useState<boolean>(false);
  const [showAdmissionMenu, setShowAdmissionMenu] = useState<boolean>(false);
  const [showStudentMenu, setShowStudentMenu] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const open = (name: string) => setOpenMenu(name);
  const close = () => setOpenMenu(null);

  function openNav(): void {
    const mySidenav = document.getElementById("mySidenav");
    const navTray = document.getElementById("navTray");

    if (mySidenav && navTray) {
      mySidenav.style.width = "270px";
      navTray.style.width = "1400px";
      setNavState(!navState);
    }
  }

  const closeNav = useCallback(() => {
    const mySidenav = document.getElementById("mySidenav");
    const navTray = document.getElementById("navTray");

    if (mySidenav && navTray) {
      mySidenav.style.width = "0";
      navTray.style.width = "0";
      setNavState(!navState);
    }
  }, [navState]);

  const handleEscape = useCallback(() => {
    return (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenModal(false);
        setOpenMenu(null);
        closeNav();
      }
    };
  }, [closeNav]);

  function closeNavTray(): void {
    const mySidenav = document.getElementById("mySidenav");
    const navTray = document.getElementById("navTray");
    if (navState && mySidenav && navTray) {
      mySidenav.style.width = "0";
      navTray.style.width = "0";
      setNavState(!navState);
    } else {
      console.error("Elements not found: mySidenav or navTray or NavState");
    }
  }

  function toggleAdmissionDropdown() {
    setAdmissionDropdown(!admissionDropdown);
  }

  function toggleAboutDropdown() {
    setAboutDropdown(!aboutDropdown);
  }

  function toggleStudentDropdown() {
    setStudentDropdown(!studentDropdown);
  }

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrolling(true);
      } else {
        setScrolling(false);
      }
    };

    const escapeHandler = handleEscape();
    document.addEventListener("keydown", escapeHandler);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("keydown", escapeHandler);
    };
  }, [navState, handleEscape]);

  // Manage focus + focusability for the mobile navigation tray.
  // When closed, mark the tray `inert` so its links are removed from the tab
  // order and hidden from assistive tech; when open, move focus into it and
  // return focus to the hamburger button on close. [SC 2.4.3], [SC 4.1.2]
  const prevNavState = useRef(false);
  useEffect(() => {
    // Apply `inert` to the sidenav dialog (which holds the menu links), NOT the
    // whole navTray — the navTray also wraps the hamburger toggle, and making it
    // inert while closed would disable the very button used to open the menu.
    const sidenav = sidenavRef.current as (HTMLDivElement & { inert?: boolean }) | null;
    if (sidenav) sidenav.inert = !navState;

    if (navState) {
      const focusables =
        sidenavRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      focusables?.[0]?.focus();
    } else if (prevNavState.current) {
      // Only when transitioning from open -> closed (not on initial mount).
      hamburgerRef.current?.focus?.();
    }
    prevNavState.current = navState;
  }, [navState]);

  const AccountButton = () => (
    <Link href="/account">
      <Button
        size="sm"
        className="cursor-pointer bg-gradient-to-r from-healing-600 to-wisdom-600 hover:from-healing-700 hover:to-wisdom-700 text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold px-5"
      >
        <GraduationCap className="w-4 h-4 mr-1" />
        Account
      </Button>
    </Link>
  );

  const LightAccountButtonMobile = () => (
    <Link href="/account" onClick={closeNav} className={styles.accountMobile}>
      <GraduationCap className="w-4 h-4" aria-hidden="true" />
      <span>Account</span>
    </Link>
  );

  const LightAccountButton = () => (
    <Link href="/account">
      <Button
        size="sm"
        className="cursor-pointer bg-white/90 text-healing-800 hover:bg-white hover:text-healing-900 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold px-5 border border-white/30 backdrop-blur-sm"
      >
        <GraduationCap className="w-4 h-4 mr-1" />
        Account
      </Button>
    </Link>
  );

  return (
    <nav aria-label="Primary">
      <Modal
        setOpenModal={setOpenModal}
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
      />

      <div
        className={`${scrolling ? `${styles.nav_scrolled}` : ""} ${
          styles.nav_container
        } ${lightBg ? `${styles.black}` : ""}`}
        style={bg ? { backgroundColor: "rgb(0, 104, 55)" } : {}}
      >
        <div className={styles.top_bar}>
          <span aria-hidden="true">
            <MailIcon />
          </span>{" "}
          <span>
            For enquiries, contact us at{" "}
            <a className={styles.mailto} href="mailto:info@theoicnm.com">
              info@theoicnm.com
            </a>
          </span>
        </div>
        <div className={styles.nav_inner}>
          <div className={styles.nav_inner_right}>
            {lightBg ? (
              <Link
                href="/"
                aria-label="Olive International College of Natural Medicine — home"
              >
                <div className="hidden md:block">
                  <BlackLogo />
                </div>
                <div className="md:hidden mt-3">
                  <BlackLogoMobile />
                </div>
              </Link>
            ) : (
              <Link
                href="/"
                aria-label="Olive International College of Natural Medicine — home"
              >
                <div className="hidden md:block">
                  <Logo />
                </div>
                <div className="md:hidden mt-3">
                  <LogoMobile />
                </div>
              </Link>
            )}
            <ul>
              {/* focus/blur emulate :focus-within so keyboard users can open the
                  submenu; the actual controls inside are links. */}
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
              <li
                onMouseEnter={() => open("about")}
                onMouseLeave={close}
                onFocus={() => open("about")}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node))
                    close();
                }}
                className={styles.admissionMenu_main}
              >
                <Link
                  href="/about-us"
                  aria-haspopup="true"
                  aria-expanded={openMenu === "about"}
                  aria-current={pathname === "/about-us" ? "page" : undefined}
                >
                  About{" "}
                  <span aria-hidden="true">
                    {lightBg ? (
                      <BlackArrowDown className={styles.arrowDown} />
                    ) : (
                      <ArrowDown className={styles.arrowDown} />
                    )}
                  </span>
                </Link>
                {openMenu === "about" && (
                  <ul className={styles.admissionSubMenu}>
                    <li>
                      <Link className={styles.sublink} href="/about-us">
                        About us
                      </Link>
                    </li>
                    <li>
                      <Link
                        className={styles.sublink}
                        href="/college-leadership"
                      >
                        College Leadership
                      </Link>
                    </li>
                    <li>
                      <Link className={styles.sublink} href="/faculty-members">
                        Faculty members
                      </Link>
                    </li>
                    <li>
                      <Link className={styles.sublink} href="/alumni">
                        Alumni
                      </Link>
                    </li>
                    <li>
                      <Link className={styles.sublink} href="/book-appointment">
                        Book Appointment
                      </Link>
                    </li>
                    <li>
                      <Link className={styles.sublink} href="/about-us#faq">
                        FAQ
                      </Link>
                    </li>
                  </ul>
                )}
              </li>
              <li>
                <Link
                  href="/programmes"
                  aria-current={pathname === "/programmes" ? "page" : undefined}
                >
                  Programmes
                </Link>
              </li>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
              <li
                onMouseEnter={() => open("admission")}
                onMouseLeave={close}
                onFocus={() => open("admission")}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node))
                    close();
                }}
                className={styles.admissionMenu_main}
              >
                <Link
                  href="/admission"
                  aria-haspopup="true"
                  aria-expanded={openMenu === "admission"}
                  aria-current={pathname === "/admission" ? "page" : undefined}
                >
                  Admission{" "}
                  <span aria-hidden="true">
                    {lightBg ? (
                      <BlackArrowDown className={styles.arrowDown} />
                    ) : (
                      <ArrowDown className={styles.arrowDown} />
                    )}
                  </span>
                </Link>
                {openMenu === "admission" && (
                  <ul className={styles.admissionSubMenu}>
                    <li>
                      <Link className={styles.sublink} href="/admission">
                        Application Procedure
                      </Link>
                    </li>
                    <li>
                      <Link
                        className={styles.sublink}
                        href="/examination-grading-system"
                      >
                        Examination and Grading System
                      </Link>
                    </li>
                    <li>
                      <Link className={styles.sublink} href="/tuition-fees">
                        Tuition Fees
                      </Link>
                    </li>
                    <li>
                      <Link className={styles.sublink} href="/foreign-students">
                        Foreign Students
                      </Link>
                    </li>
                    <li>
                      <Link className={styles.sublink} href="/scholarship">
                        Scholarship and Financial Aids
                      </Link>
                    </li>
                    <li>
                      <Link className={styles.sublink} href="/downloads">
                        Resource Downloads
                      </Link>
                    </li>
                  </ul>
                )}
              </li>
              <li>
                <Link
                  href="/accreditation"
                  aria-current={
                    pathname === "/accreditation" ? "page" : undefined
                  }
                >
                  Accreditation
                </Link>
              </li>
              <li>{lightBg ? <AccountButton /> : <LightAccountButton />}</li>
            </ul>
          </div>
          <div className="hidden md:inline-block mx-10">
            <Search
              lightBg={lightBg}
              openModal={openModal}
              setOpenModal={setOpenModal}
            />
          </div>
        </div>
        <div className={styles.bottom_bar}></div>
      </div>
      {/* Backdrop closes the tray on click as a pointer convenience; keyboard
          users have the close button and the Escape key. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        ref={navTrayRef}
        onClick={() => closeNavTray()}
        id="navTray"
        className={styles.navTray}
      >
        {/* Menu Icon Harmbrger */}
        <MenuHarmburger
          lightBg={lightBg}
          openNav={openNav}
          closeNav={closeNav}
          navState={navState}
          openModal={openModal}
          setOpenModal={setOpenModal}
          hamburgerRef={hamburgerRef}
        />
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <div
          ref={sidenavRef}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key !== "Tab") return;
            const focusables = sidenavRef.current?.querySelectorAll<HTMLElement>(
              FOCUSABLE_SELECTOR
            );
            if (!focusables || focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }}
          id="mySidenav"
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
          className={styles.sidenav}
        >
          <div style={{ marginLeft: "10px" }}>
            <Link
              onClick={() => closeNavTray()}
              href="/"
              aria-label="Olive International College of Natural Medicine — home"
            >
              <Logo />
            </Link>
          </div>
          <ul>
            <li>
              <button
                type="button"
                onClick={toggleAboutDropdown}
                aria-expanded={aboutDropdown}
              >
                About{" "}
                <span aria-hidden="true">
                  <ArrowDown className={styles.arrowDown} />
                </span>
              </button>
            </li>
            {aboutDropdown && (
              <>
                <li style={{ marginLeft: "25px" }}>
                  <Link onClick={() => closeNavTray()} href="/about-us">
                    About Us
                  </Link>
                </li>
                <li style={{ marginLeft: "25px" }}>
                  <Link
                    onClick={() => closeNavTray()}
                    href="/college-leadership"
                  >
                    College Leadership
                  </Link>
                </li>
                <li style={{ marginLeft: "25px" }}>
                  <Link onClick={() => closeNavTray()} href="/faculty-members">
                    Faculty members
                  </Link>
                </li>
                <li style={{ marginLeft: "25px" }}>
                  <Link
                    onClick={() => closeNavTray()}
                    className={styles.sublink}
                    href="/alumni"
                  >
                    Alumni
                  </Link>
                </li>

                <li style={{ marginLeft: "25px" }}>
                  <Link
                    onClick={() => closeNavTray()}
                    className={styles.sublink}
                    href="/book-appointment"
                  >
                    Book Appointment
                  </Link>
                </li>
                <li style={{ marginLeft: "25px" }}>
                  <Link
                    onClick={() => closeNavTray()}
                    className={styles.sublink}
                    href="/about-us#faq"
                  >
                    FAQ
                  </Link>
                </li>
              </>
            )}
            <li>
              <Link onClick={() => closeNavTray()} href="/programmes">
                Programmes
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={toggleAdmissionDropdown}
                aria-expanded={admissionDropdown}
              >
                Admission{" "}
                <span aria-hidden="true">
                  <ArrowDown className={styles.arrowDown} />
                </span>
              </button>
            </li>
            {admissionDropdown && (
              <>
                <li style={{ marginLeft: "25px" }}>
                  <Link onClick={() => closeNavTray()} href="/admission">
                    Application Procedure
                  </Link>
                </li>
                <li style={{ marginLeft: "25px" }}>
                  <Link
                    className={styles.sublink}
                    href="/examination-grading-system"
                    onClick={() => closeNavTray()}
                  >
                    Examination and <br />
                    Grading System
                  </Link>
                </li>
                <li style={{ marginLeft: "25px" }}>
                  <Link
                    onClick={() => closeNavTray()}
                    className={styles.sublink}
                    href="/tuition-fees"
                  >
                    Tuition Fees
                  </Link>
                </li>
                <li style={{ marginLeft: "25px" }}>
                  <Link
                    onClick={() => closeNavTray()}
                    className={styles.sublink}
                    href="/foreign-students"
                  >
                    Foreign Students
                  </Link>
                </li>
                <li style={{ marginLeft: "25px" }}>
                  <Link onClick={() => closeNavTray()} href="/scholarship">
                    Scholarship and <br />
                    Financial Aids
                  </Link>
                </li>
                <li style={{ marginLeft: "25px" }}>
                  <Link onClick={() => closeNavTray()} href="/downloads">
                    Resource Downloads
                  </Link>
                </li>
              </>
            )}
            <li>
              <Link onClick={() => closeNavTray()} href="/accreditation">
                Accreditation
              </Link>
            </li>
            <li>
              <LightAccountButtonMobile />
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}

const Search = ({ lightBg, setOpenModal, openModal }: SearchProps) => {
  return (
    <div className={`${styles.nav_inner_left}`}>
      <button
        type="button"
        aria-label="Search the site"
        aria-haspopup="dialog"
        aria-expanded={openModal}
        onClick={() => setOpenModal(!openModal)}
        className={`${styles.search_icon} bg-transparent border-0 p-0 cursor-pointer inline-flex items-center`}
      >
        {lightBg ? (
          <BlackSearchIcon className={styles.searchIcon} />
        ) : (
          <SearchIcon className={styles.searchIcon} />
        )}
      </button>
    </div>
  );
};

const MenuHarmburger = ({
  openNav,
  closeNav,
  lightBg,
  navState,
  openModal,
  setOpenModal,
  hamburgerRef,
}: MenuHarburgerProps) => {
  return (
    <div style={{ position: "fixed", top: "60px", right: "10px" }}>
      {navState ? (
        <button
          type="button"
          aria-label="Close menu"
          aria-controls="mySidenav"
          aria-expanded={true}
          onClick={() => closeNav()}
          className={`${styles.closebtn} bg-transparent border-0 p-0 cursor-pointer inline-flex items-center`}
        >
          <span aria-hidden="true">
            <CloseIcon lightBg={lightBg} />
          </span>
        </button>
      ) : (
        <div className="flex flex-row justify-center items-center">
          <div className="md:hidden mx-10 mt-2">
            <Search
              lightBg={lightBg}
              openModal={openModal}
              setOpenModal={setOpenModal}
            />
          </div>
          <button
            ref={hamburgerRef}
            type="button"
            aria-label="Open menu"
            aria-controls="mySidenav"
            aria-expanded={false}
            aria-haspopup="dialog"
            onClick={() => openNav()}
            className={`${styles.hamburger} bg-transparent border-0 p-0 cursor-pointer`}
          >
            <span aria-hidden="true">
              <HamburgerIcon lightBg={lightBg} />
            </span>
          </button>
        </div>
      )}
    </div>
  );
};
